// src/components/theme/elements/ThemeNavDirection.js

import React, { useState, useEffect, useRef } from 'react';
import { Group, Image as KonvaImage, Rect as KonvaRect } from 'react-konva';
import PropTypes from 'prop-types';
import Konva from 'konva';
import { toast } from 'react-toastify';
import CustomColorPicker from '../CustomColorPicker';
import { hexToRgb, initializeColorReplaceFilter } from '../ThemeColorTools';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';


/**
 * ThemeNavDirectionSettings Component
 * Provides UI controls to configure the ThemeNavDirection element.
 * Includes additional color settings that do not affect the image.
 */
export const ThemeNavDirectionSettings = ({ element, updateElement }) => {

  /**
   * Handler for X position change.
   * Updates the X position of the element.
   */
  const handleXChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x) && x >= 0) {
      updateElement(element.id, 'x', x);
    }
  };

  /**
   * Handler for Y position change.
   * Updates the Y position of the element.
   */
  const handleYChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y) && y >= 0) {
      updateElement(element.id, 'y', y);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {/* X Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          style={{ marginLeft: '10px', width: '80px' }}
          aria-label="Set X Position"
        />
      </label>

      {/* Y Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          style={{ marginLeft: '10px', width: '80px' }}
          aria-label="Set Y Position"
        />
      </label>

      {/* Color */}
      <CustomColorPicker
        label="Color"
        color={element.color}
        onChange={(value) => { updateElement(element.id, 'color', value); }}
      />

    </div>
  );
};

ThemeNavDirectionSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    color: PropTypes.string,         // Road Line Color (optional)
    isEnabled: PropTypes.bool,               // Enabled state (optional)
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
};

/**
 * ThemeNavDirection Component
 * Renders the Navigation direction image on the canvas with color replacement.
 */
const ThemeNavDirection = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  render,editAllowed,
  scale,
  snapToGridEnabled,
  gridSize
}) => {
  const [imageObj, setImageObj] = useState(null);
  const imageRef = useRef();

  // Fixed dimensions for the image
  const fixedWidth = 60;
  const fixedHeight = 60;

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  /**
   * Load the Navigation direction image.
   * Sets the image object once loaded.
   */
  useEffect(() => {
    initializeColorReplaceFilter();
    const img = new window.Image();
    img.src = './images_theme/nav-direction.png'; // Path to your Navigation direction image
    img.crossOrigin = 'Anonymous'; // To handle cross-origin issues if any
    img.onload = () => {
      setImageObj(img);
      console.log('Navigation direction image loaded successfully.', img);
    };
    img.onerror = () => {
      toast.error('Failed to load Navigation direction image.');
    };
  }, []);

  /**
   * Cache the image and redraw the layer when imageObj or color changes.
   */
  useEffect(() => {
    console.log('Current tintColor:', element.color); // Debugging
    if (imageObj && imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer().batchDraw();
    }
  }, [imageObj, element.color]);

  /**
   * Handler for drag end event.
   * Updates the X and Y positions of the element.
   */
  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();
    updateElement(element.id, 'x', Math.round(x));
    updateElement(element.id, 'y', Math.round(y));
  };

  /**
   * Re-apply cache and redraw when imageObj or color changes.
   */
  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer().batchDraw();
    }
  }, [imageObj, element.color]);

  /**
   * Determine the current color based on the color prop.
   */
  const currentColor = element.color || '#ffffff'; // Default to white if no color is set

  return (
    <Group
      x={element.x}
      y={element.y}
      draggable={!render && editAllowed} // Make draggable only when render is false
      onClick={() => setSelectedElement(element)}
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, fixedWidth, fixedHeight) }}
      onTap={() => setSelectedElement(element)}
      onDragEnd={handleDragEnd}
      aria-label="Navigation direction Indicator"
    >
      {imageObj && (
        <KonvaImage
          image={imageObj}
          width={fixedWidth} // Set fixed width
          height={fixedHeight} // Set fixed height
          filters={[Konva.Filters.ColorReplaceFilter]} // Use the custom filter
          tintColor={hexToRgb(currentColor)} // Pass the current color
          ref={imageRef}
          onLoad={() => {
            if (imageRef.current) {
              imageRef.current.cache();
              imageRef.current.getLayer().batchDraw();
            }
          }}
        />
      )}
      {/* Add the debug blue rectangle */}
      {!render && (
        <KonvaRect
          x={0}
          y={0}
          width={fixedWidth}
          height={fixedHeight}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [1, 1] : [0, 0]}
          listening={false} // Ensure it doesn't capture pointer events
        />
      )}
    </Group>
  );
};

ThemeNavDirection.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    color: PropTypes.string,         // Road Line Color (optional)
    isEnabled: PropTypes.bool,       // Enabled state (optional)
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  selectedElement: PropTypes.shape({
    id: PropTypes.string.isRequired,
    // ... other properties as needed
  }),
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  render: PropTypes.bool.isRequired, // Determines if the component is in render mode
  editAllowed: PropTypes.bool,
};

export default ThemeNavDirection;
